import React, { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  MenuItem,
  TextField,
  Stack,
  Chip,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ScreenRotationOutlinedIcon from "@mui/icons-material/ScreenRotationOutlined";
import dayjs from "dayjs";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { pageFieldSx } from "../../styles/pageModernStyles";
import TotalConPct from "./TotalConPct";

const PedidosIrene = ({ resumenRows, year, setYear, empresa, setEmpresa }) => {
  const { contentMargin, isMobile } = useContext(SidebarContext);
  const isLandscape = useMediaQuery("(orientation: landscape)", {
    noSsr: true,
    defaultMatches: false,
  });
  const showRotatePrompt = isMobile && !isLandscape;

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const meses = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const [customerFilter, setCustomerFilter] = useState("");

  const filteredRows = resumenRows;

  // 🔹 Agrupar por REPALIAS + CLIPRO
  const resumenGrouped = {};
  filteredRows.forEach((row) => {
    if (!row.REPALIAS || !row.CLIPRO) return;
    const key = `${row.REPALIAS}__${row.CLIPRO}`;
    const month = Number(row.MONTH);
    const tipoCont = Number(row.TIPOCONT) || 1;
    if (!resumenGrouped[key]) {
      resumenGrouped[key] = {
        REPALIAS: row.REPALIAS,
        CLIPRO: row.CLIPRO,
        TIPOCONT: tipoCont,
        meses: {},
      };
    }

    const venta = Number(row.Sales) || 0;

    if (!resumenGrouped[key].meses[month]) {
      resumenGrouped[key].meses[month] = {
        MONTH: month,
        Venta: 0,
        TIPOCONT: tipoCont,
      };
    } else {
      if (tipoCont === 2) {
        resumenGrouped[key].meses[month].TIPOCONT = 2;
      }
    }

    if (tipoCont === 2) {
      resumenGrouped[key].TIPOCONT = 2;
    }

    resumenGrouped[key].meses[month].Venta += venta;
  });

  const resumenArray = Object.values(resumenGrouped);

  const normalizarTexto = (txt) =>
    String(txt || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const filtro = normalizarTexto(customerFilter);
  const filteredByCustomer = filtro
    ? resumenArray.filter((g) => {
        const customer = normalizarTexto(g.CLIPRO);
        const am = normalizarTexto(g.REPALIAS);
        return customer.includes(filtro) || am.includes(filtro);
      })
    : resumenArray;

  // 🔹 Agrupar por REPALIAS
  const reps = {};
  filteredByCustomer.forEach((g) => {
    if (!reps[g.REPALIAS]) reps[g.REPALIAS] = [];
    reps[g.REPALIAS].push(g);
  });

  const granTotalMeses = meses.reduce((acc, mes) => {
    acc[mes] = filteredByCustomer.reduce(
      (sum, g) => sum + (Number(g.meses?.[mes]?.Venta) || 0),
      0,
    );
    return acc;
  }, {});
  const granTotalAnual = Object.values(granTotalMeses).reduce(
    (sum, val) => sum + val,
    0,
  );

  const totalClientes = filteredByCustomer.length;
  const totalReps = Object.keys(reps).length;

  const fieldSx = pageFieldSx(theme);

  const stickyBg =
    theme.palette.estimates?.main ??
    alpha(theme.palette.text.primary, isDark ? 0.06 : 0.03);

  // Color semántico de cada celda mensual
  const cellColorFor = (venta, tipocont) => {
    if (venta < 0) return theme.palette.error.main;
    if (tipocont === 1) return theme.palette.info.main;
    if (tipocont === 2) return theme.palette.success.main;
    return theme.palette.warning.main;
  };

  const shellSx = {
    ...contentMargin,
    boxSizing: "border-box",
    width: "calc(100% - 20px)",
    height: "100dvh",
    maxHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    m: 0,
    ml: 1,
    p: isMobile ? 0.75 : 0,
  };

  if (showRotatePrompt) {
    return (
      <Box
        sx={{
          ...shellSx,
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            maxWidth: 320,
            textAlign: "center",
            p: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, isDark ? 0.35 : 0.08)}`,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 2,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              animation: "ventasTotalesRotateHint 2.4s ease-in-out infinite",
              "@keyframes ventasTotalesRotateHint": {
                "0%, 100%": { transform: "rotate(0deg)" },
                "35%, 65%": { transform: "rotate(90deg)" },
              },
            }}
          >
            <ScreenRotationOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Gira el dispositivo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ventas totales está optimizada para móvil en horizontal. Rota el
            teléfono para ver la matriz anual.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      {/* Hero header (fuera del scroll de la tabla) */}
      <Box
        sx={{
          flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            isDark ? 0.18 : 0.08,
          )} 0%, ${alpha(
            theme.palette.background.paper,
            0.96,
          )} 60%, ${theme.palette.background.paper} 100%)`,
          border: "1px solid",
          borderColor: "divider",
          borderTop: isMobile ? undefined : 0,
          borderLeft: isMobile ? undefined : 0,
          borderRight: isMobile ? undefined : 0,
          borderTopLeftRadius: isMobile ? 10 : 0,
          borderTopRightRadius: isMobile ? 10 : 0,
          px: isMobile ? 1 : { xs: 1.5, md: 2 },
          py: isMobile ? 0.75 : 1.25,
        }}
      >
        <Stack
          direction={isMobile ? "row" : { xs: "column", md: "row" }}
          spacing={isMobile ? 1 : 1.5}
          alignItems={isMobile ? "center" : { xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          {/* Identidad */}
          <Stack
            direction="row"
            spacing={isMobile ? 1 : 1.5}
            alignItems="center"
          >
            <Box
              sx={{
                width: isMobile ? 36 : 44,
                height: isMobile ? 36 : 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: theme.palette.primary.contrastText,
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.45)}`,
                flexShrink: 0,
              }}
            >
              <LeaderboardOutlinedIcon
                fontSize={isMobile ? "small" : "medium"}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {!isMobile ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label="CRM"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    }}
                  />
                  <Chip
                    label={`${totalReps} AM · ${totalClientes} clientes`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      backgroundColor: alpha(theme.palette.text.primary, 0.06),
                      color: theme.palette.text.secondary,
                    }}
                  />
                </Stack>
              ) : null}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: -0.2,
                  color: theme.palette.text.primary,
                  fontSize: isMobile
                    ? "0.95rem"
                    : { xs: "1.05rem", md: "1.25rem" },
                  whiteSpace: "nowrap",
                }}
              >
                Anual Sales{" "}
                <Box
                  component="span"
                  sx={{ color: theme.palette.primary.main }}
                >
                  · {year}
                </Box>
              </Typography>
              {isMobile ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {totalReps} AM · {totalClientes} clientes · desliza
                  horizontalmente
                </Typography>
              ) : null}
            </Box>
          </Stack>

          {/* Filtros */}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              flexShrink: 0,
              flexWrap: isMobile ? "nowrap" : "wrap",
              width: isMobile ? "auto" : { xs: "100%", sm: "auto" },
            }}
          >
            <TextField
              size="small"
              placeholder={isMobile ? "Buscar…" : "Buscar cliente o AM…"}
              variant="outlined"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              sx={{
                ...fieldSx,
                minWidth: isMobile ? 120 : { xs: "100%", sm: 240 },
                maxWidth: isMobile ? 160 : undefined,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
              sx={{
                ...fieldSx,
                minWidth: isMobile ? 108 : 150,
              }}
              InputProps={{
                startAdornment: isMobile ? undefined : (
                  <InputAdornment position="start">
                    <BusinessOutlinedIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="stage">STAGE</MenuItem>
              <MenuItem value="erna">ERNA</MenuItem>
              <MenuItem value="fusion">
                {isMobile ? "TOTAL" : "TOTAL (STAGE+ERNA)"}
              </MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{
                ...fieldSx,
                minWidth: isMobile ? 84 : 120,
              }}
              InputProps={{
                startAdornment: isMobile ? undefined : (
                  <InputAdornment position="start">
                    <CalendarMonthOutlinedIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
            >
              {[...Array(6)].map((_, i) => {
                const y = dayjs().year() - i;
                return (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                );
              })}
            </TextField>
          </Stack>
        </Stack>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          border: "1px solid",
          borderTop: 0,
          borderLeft: isMobile ? undefined : 0,
          borderRight: isMobile ? undefined : 0,
          borderBottom: isMobile ? undefined : 0,
          borderColor: "divider",
          borderBottomLeftRadius: isMobile ? 10 : 0,
          borderBottomRightRadius: isMobile ? 10 : 0,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            "& .MuiTableCell-root": {
              borderColor: alpha(theme.palette.divider, 0.6),
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 4,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  minWidth: 160,
                  maxWidth: 160,
                }}
              >
                Account Manager
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  left: 160,
                  top: 0,
                  zIndex: 4,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  minWidth: 200,
                  maxWidth: 200,
                }}
              >
                Customer
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.paper,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  minWidth: 60,
                  maxWidth: 60,
                }}
              >
                Type
              </TableCell>
              {meses.map((mes) => (
                <TableCell
                  key={mes}
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                    color: theme.palette.text.secondary,
                    backgroundColor: alpha(
                      theme.palette.primary.main,
                      isDark ? 0.12 : 0.06,
                    ),
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  {dayjs()
                    .month(mes - 1)
                    .format("MMM")}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: theme.palette.primary.contrastText,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  minWidth: 168,
                }}
              >
                Total Anual
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={meses.length + 4}
                  align="center"
                  sx={{
                    py: 4,
                    color: theme.palette.text.secondary,
                    fontStyle: "italic",
                  }}
                >
                  No hay datos para {year}
                </TableCell>
              </TableRow>
            ) : (
              Object.entries(reps).map(([rep, clientes]) => (
                <React.Fragment key={rep}>
                  {clientes.map((g, i) => {
                    let totalCliente = 0;
                    const repAlias = (g.REPALIAS || "").toString().trim();
                    const hasRep = repAlias.length > 0;
                    return (
                      <TableRow
                        key={i}
                        sx={{
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              isDark ? 0.08 : 0.04,
                            ),
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            backgroundColor: stickyBg,
                            zIndex: 1,
                            minWidth: 160,
                            maxWidth: 160,
                            whiteSpace: "nowrap",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: hasRep
                              ? theme.palette.text.secondary
                              : theme.palette.warning.main,
                            fontStyle: hasRep ? "normal" : "italic",
                          }}
                        >
                          {hasRep ? repAlias : "— Sin AM —"}
                        </TableCell>
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 160,
                            backgroundColor: stickyBg,
                            zIndex: 1,
                            minWidth: 300,
                            maxWidth: 300,
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {g.CLIPRO}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            backgroundColor: stickyBg,
                            minWidth: 40,
                            maxWidth: 40,
                          }}
                        >
                          <Chip
                            label={g.TIPOCONT}
                            size="small"
                            sx={{
                              height: 20,
                              minWidth: 28,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              backgroundColor:
                                Number(g.TIPOCONT) === 2
                                  ? alpha(theme.palette.success.main, 0.18)
                                  : alpha(theme.palette.info.main, 0.18),
                              color:
                                Number(g.TIPOCONT) === 2
                                  ? theme.palette.success.main
                                  : theme.palette.info.main,
                              border: "1px solid",
                              borderColor:
                                Number(g.TIPOCONT) === 2
                                  ? alpha(theme.palette.success.main, 0.4)
                                  : alpha(theme.palette.info.main, 0.4),
                            }}
                          />
                        </TableCell>
                        {meses.map((mes) => {
                          const m = g.meses[mes] || {};
                          const venta = Number(m?.Venta) || 0;
                          const tipocont = Number(m?.TIPOCONT || 1);
                          totalCliente += venta;

                          const cellColor = cellColorFor(venta, tipocont);
                          const hasValue = venta !== 0;

                          return (
                            <TableCell key={mes} align="right" sx={{ p: 0.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  p: 0.5,
                                  borderRadius: 1.25,
                                  minWidth: "6.5em",
                                  border: "1px solid",
                                  borderColor: hasValue
                                    ? alpha(cellColor, 0.25)
                                    : "transparent",
                                  backgroundColor: hasValue
                                    ? alpha(cellColor, isDark ? 0.12 : 0.06)
                                    : "transparent",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      cellColor,
                                      isDark ? 0.22 : 0.14,
                                    ),
                                    borderColor: alpha(cellColor, 0.5),
                                  },
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    color: hasValue
                                      ? cellColor
                                      : theme.palette.text.disabled,
                                    fontSize: "0.78rem",
                                  }}
                                >
                                  {hasValue
                                    ? venta.toLocaleString("es-ES", {
                                        maximumFractionDigits: 2,
                                      })
                                    : "—"}
                                </Typography>
                              </Box>
                            </TableCell>
                          );
                        })}

                        <TableCell
                          align="right"
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              isDark ? 0.1 : 0.05,
                            ),
                          }}
                        >
                          <TotalConPct
                            valor={totalCliente}
                            referencia={granTotalAnual}
                            valueColor={
                              totalCliente < 0
                                ? theme.palette.error.main
                                : theme.palette.primary.main
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Totales por rep */}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        letterSpacing: 0.4,
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        backgroundImage: `linear-gradient(${alpha(
                          theme.palette.warning.main,
                          isDark ? 0.16 : 0.1,
                        )}, ${alpha(
                          theme.palette.warning.main,
                          isDark ? 0.16 : 0.1,
                        )})`,
                        borderTop: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                        borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                      }}
                    >
                      Σ Totales · {rep}
                    </TableCell>

                    {(() => {
                      let totalAnual = 0;
                      return (
                        <>
                          {meses.map((mes) => {
                            const t = clientes.reduce(
                              (acc, g) => {
                                const m = g.meses[mes] || {};
                                const venta = Number(m?.Venta) || 0;
                                acc.venta += venta;
                                return acc;
                              },
                              { venta: 0 },
                            );

                            totalAnual += t.venta;

                            return (
                              <TableCell
                                key={mes}
                                align="right"
                                sx={{
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  backgroundColor: alpha(
                                    theme.palette.warning.main,
                                    isDark ? 0.14 : 0.08,
                                  ),
                                  borderTop: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                                  borderBottom: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                                }}
                              >
                                {t.venta.toLocaleString("es-ES")}
                              </TableCell>
                            );
                          })}

                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 800,
                              color: theme.palette.primary.contrastText,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            }}
                          >
                            <TotalConPct
                              valor={totalAnual}
                              referencia={granTotalAnual}
                              variant="inverted"
                            />
                          </TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                </React.Fragment>
              ))
            )}

            {filteredByCustomer.length > 0 && (
              <TableRow>
                <TableCell
                  colSpan={3}
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    fontWeight: 800,
                    fontSize: "0.85rem",
                    letterSpacing: 0.4,
                    color: theme.palette.primary.contrastText,
                    backgroundColor: theme.palette.primary.dark,
                    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  }}
                >
                  ◆ Gran Total · todas las AM
                </TableCell>
                {meses.map((mes) => (
                  <TableCell
                    key={`grand-${mes}`}
                    align="right"
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 800,
                      color: theme.palette.primary.contrastText,
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    }}
                  >
                    {granTotalMeses[mes].toLocaleString("es-ES")}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 800,
                    color: theme.palette.primary.contrastText,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.dark, 0.85)} 100%)`,
                    boxShadow: `inset 0 0 0 1px ${alpha(
                      theme.palette.common.white,
                      0.15,
                    )}`,
                  }}
                >
                  {granTotalAnual.toLocaleString("es-ES")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PedidosIrene;
